import {
  AppBar,
  Button,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  Toolbar,
} from '@mui/material';
import Badge from '@mui/material/Badge';
import { withStyles } from 'tss-react/mui';

import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { AccountCircle as ProfileIcon, Help } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileDialog from './ProfileDialog';
import LogoutDialog from './LogoutDialog';
import BetaToggle from '../../modules/global/components/BetaToggle/BetaSwitch';
import { resetTab } from '../../store/actions/tabActions';
import {
  getChangedTab,
  getPendingReleaseCount,
  getSelectedUserType,
  getUserProfile,
} from '../../store/selectors/selectors';
import { BaseRoleCodes, LABEL, TABS } from '../../theme/constants';
import UserProfileSelect from './UserProfileSelect';
import { logOut } from '../../auth/hooks/authProvider';
import { hasAnyBaseRole, hasBaseRole } from 'auth/helper';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useBetaHook } from 'modules/global/hooks';

const styles = (theme) => ({
  logo: {
    flexGrow: 1,
  },
  toolbar: {
    paddingRight: 24,
    backgroundImage: 'linear-gradient(to right, #005efd, #009dfe)',
  },
  appBar: {
    zIndex: theme.zIndex.drawer - 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  menuButton: {
    marginLeft: 12,
  },
  profileIcon: {
    marginRight: 5,
  },
  title: {
    flexGrow: 1,
  },
  row: {
    borderStyle: 'hidden',
    marginLeft: '15px',
    display: 'inline-table',
  },
  cellText: {
    cursor: 'pointer',
    padding: '10px',
    borderRadius: '3px',
    color: 'white',
  },
  cellTextActive: {
    cursor: 'pointer',
    backgroundColor: '#004bca',
    padding: '10px',
    borderRadius: '3px',
    color: 'white',
  },
  flex: {
    flex: 1,
  },
  cell: {
    padding: '12px',
  },
  userMenu: {
    '& > *': {
      margin: '5px',
    },
  },
});

const Topbar = (props) => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);
  const [activeTab, setActiveTab] = useState(
    location.pathname === '/' ? '/containers' : location.pathname
  );
  const { isBetaEnabled, toggleBeta } = useBetaHook();
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const navigate = useNavigate();
  const { classes, userProfile, pendingReleaseCount } = props;

  useEffect(() => {
    if (props.changedTab) {
      const pathname = location.pathname;
      setActiveTab(pathname);
      navigate(pathname);
      props.resetTab();
    }
  }, [props.changedTab, props.resetTab, props.changedTab, navigate, location.pathname]);

  useEffect(() => {
    setActiveTab(location.pathname === '/' ? '/containers' : location.pathname);
  }, [location.pathname]);

  const containersTab = () => {
    setActiveTab('/containers');
    navigate('/containers');
  };

  const subscriptionTab = () => {
    setActiveTab('/container-subscriptions');
    navigate('/container-subscriptions');
  };

  const driverlistTab = () => {
    setActiveTab('/driverlist');
    navigate('/driverlist');
  };

  const releasesTab = () => {
    setActiveTab('/releases');
    navigate('/releases');
  };

  const handleProfileMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleHelpMenuClick = (event) => {
    setAnchorEl2(event.currentTarget);
  };

  const handleHelpMenuClose = () => {
    setAnchorEl2(null);
  };

  const openUserProfile = () => {
    setAnchorEl(null);
    setShowUserProfile(!showUserProfile);
  };

  const onToggle = () => {
    toggleBeta();
  };

  const handleLogout = () => {
    setAnchorEl(null);
    setLoggingOut(true);
    setTimeout(() => {
      logOut();
    }, 500);
  };

  return (
    <>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar disableGutters className={classes.toolbar}>
          <div className={classes.list}>
            <Table size="small">
              <TableBody>
                <TableRow className={classes.row}>
                  {hasAnyBaseRole([
                    BaseRoleCodes.RELEASE_PARTY_CODE,
                    BaseRoleCodes.TRANSPORT_OPERATOR_CODE,
                  ]) &&
                    !isBetaEnabled && (
                      <TableCell className={classes.cell} onClick={containersTab}>
                        <ListItemText
                          className={
                            activeTab.indexOf('/containers') !== -1
                              ? classes.cellTextActive
                              : classes.cellText
                          }
                          primary={TABS.CONTAINER_OVERVIEW}
                        />
                      </TableCell>
                    )}
                  {hasBaseRole(BaseRoleCodes.SHIP_AGENT_CODE) && !isBetaEnabled && (
                    <TableCell className={classes.cell} onClick={containersTab}>
                      <ListItemText
                        className={
                          activeTab.indexOf('/containers') !== -1
                            ? classes.cellTextActive
                            : classes.cellText
                        }
                        primary={TABS.CONTAINER_OVERVIEW}
                      />
                    </TableCell>
                  )}
                  {hasAnyBaseRole([
                    BaseRoleCodes.TRANSPORT_OPERATOR_CODE,
                    BaseRoleCodes.RELEASE_PARTY_CODE,
                  ]) &&
                    !isBetaEnabled && (
                      <TableCell className={classes.cell} onClick={releasesTab}>
                        <Badge badgeContent={pendingReleaseCount} color="secondary">
                          <ListItemText
                            className={
                              activeTab.indexOf('/releases') !== -1
                                ? classes.cellTextActive
                                : classes.cellText
                            }
                            primary={TABS.PENDING_RELEASES}
                          />
                        </Badge>
                      </TableCell>
                    )}
                  {hasAnyBaseRole([
                    BaseRoleCodes.TRANSPORT_OPERATOR_CODE,
                    BaseRoleCodes.RELEASE_PARTY_CODE,
                  ]) &&
                    !isBetaEnabled && (
                      <TableCell className={classes.cell} onClick={subscriptionTab}>
                        <ListItemText
                          className={
                            activeTab.indexOf('/container-subscriptions') !== -1
                              ? classes.cellTextActive
                              : classes.cellText
                          }
                          primary={TABS.CONTAINER_SUBSCRIPTIONS}
                        />
                      </TableCell>
                    )}
                  {hasBaseRole(BaseRoleCodes.SHIP_AGENT_CODE) && !isBetaEnabled && (
                    <TableCell className={classes.cell} onClick={subscriptionTab}>
                      <ListItemText
                        className={
                          activeTab.indexOf('/container-subscriptions') !== -1
                            ? classes.cellTextActive
                            : classes.cellText
                        }
                        primary={TABS.CONTAINER_SUBSCRIPTIONS}
                      />
                    </TableCell>
                  )}
                  {hasBaseRole(BaseRoleCodes.TERMINAL_CODE) && (
                    <TableCell className={classes.cell}>
                      <ListItemText className={classes.cellTextActive} primary={LABEL.MY_YARD} />
                    </TableCell>
                  )}
                  {hasBaseRole(BaseRoleCodes.TRANSPORT_OPERATOR_CODE) && !isBetaEnabled && (
                    <TableCell className={classes.cell} onClick={driverlistTab}>
                      <ListItemText
                        className={
                          activeTab.indexOf('/driverlist') !== -1
                            ? classes.cellTextActive
                            : classes.cellText
                        }
                        primary={TABS.DRIVER_LIST}
                      />
                    </TableCell>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <div className={classes.logo} />

          {hasAnyBaseRole([
            BaseRoleCodes.SHIP_AGENT_CODE,
            BaseRoleCodes.TERMINAL_CODE,
            BaseRoleCodes.TRANSPORT_OPERATOR_CODE,
            BaseRoleCodes.RELEASE_PARTY_CODE,
          ]) && <BetaToggle onToggle={onToggle} />}

          {userProfile && (
            <span className={classes.userMenu}>
              <Button color="inherit" onClick={handleHelpMenuClick}>
                <Help />
              </Button>
              <Menu
                id="simple-menu-2"
                anchorEl={anchorEl2}
                open={Boolean(anchorEl2)}
                onClose={handleHelpMenuClose}
              >
                <MenuItem
                  onClick={() =>
                    window.open('https://documentation.nxtport.com/certified-pickup/', '_blank')
                  }
                >
                  Documentation
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    window.open(
                      'https://documentation.nxtport.com/certified-pickup/release-notes',
                      '_blank'
                    )
                  }
                >
                  Release Notes
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    window.open(
                      'https://nxtport.atlassian.net/servicedesk/customer/portal/9',
                      '_blank'
                    )
                  }
                >
                  Support Desk
                </MenuItem>
                <MenuItem
                  onClick={() =>
                    window.open('https://www.certifiedpickup.com/en/documentation/', '_blank')
                  }
                >
                  Terms and Conditions
                </MenuItem>
              </Menu>
              <Button color="inherit" onClick={handleProfileMenuClick}>
                <ProfileIcon className={classes.profileIcon} />
                {userProfile.firstName &&
                  userProfile.lastName &&
                  userProfile.firstName + ' ' + userProfile.lastName}
                {!userProfile.firstName && !userProfile.lastName && userProfile.nxtEntityId}
              </Button>
              <Menu
                id="simple-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleProfileMenuClose}
              >
                <MenuItem onClick={handleLogout}>{LABEL.LOGOUT}</MenuItem>
                <MenuItem onClick={openUserProfile}>{LABEL.ORGANISATION_INFO}</MenuItem>
              </Menu>
              {userProfile.roles.length > 1 && <UserProfileSelect />}
            </span>
          )}
        </Toolbar>
        <ProfileDialog openDialog={showUserProfile} userProfile={userProfile} />
      </AppBar>
      <LogoutDialog openDialog={loggingOut} />
    </>
  );
};

Topbar.propTypes = {
  userprofile: PropTypes.object,
  selectedUserType: PropTypes.string,
  classes: PropTypes.object,
};

const mapStateToProps = (state) => ({
  userProfile: getUserProfile(state),
  selectedUserType: getSelectedUserType(state),
  pendingReleaseCount: getPendingReleaseCount(state),
  changedTab: getChangedTab(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      resetTab,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(Topbar, styles));
